/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import {
    Component,
    OnInit,
    Input,
    Output,
    EventEmitter,
    AfterViewInit,
    ViewChild,
    ElementRef,
    NgZone,
    OnDestroy,
    ChangeDetectorRef,
    Inject,
    Optional
} from '@angular/core';
import { animate, AnimationBuilder, AnimationEvent, keyframes, style, transition, trigger } from '@angular/animations';
import { Dir } from '@angular/cdk/bidi';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, fromEvent } from 'rxjs';
import { filter, takeUntil, take } from 'rxjs/operators';
import { AccessibilityService } from 'crimson/lib/accessibility/accessibility.service';
import { LocaleService } from 'crimson/lib/locale-service/locale.service';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { InputModalityService } from 'input-modality/lib/input-modality.service';
import { EllipsifiedTextWithTooltipDirective } from '../../directives/ellipsified-text-with-tooltip.directive';
import { SpatialNavigationGroupDirective } from '../../directives/spatial-navigation-container.directive';
import { NV_HIG_CONFIG, NvHigConfig } from '../../hig.config';
import { FocusManager } from '../../services/focus-manager';
import {
    NavigationEvent,
    SpatialNavigationDirection
} from '../../services/spatial-navigation/spatial-navigation-polyfill';
import { SpatialNavigationService } from '../../services/spatial-navigation/spatial-navigation.service';
import { HigNavItemClick, HigNavMenuItemClick, HigNavMenuItem } from '../../types/hig-shared.types';
import {
    AnimationStateMap,
    HigSidebarItemWithNotificationAriaLabelKey,
    HigSidebarItemWithPageHighlightAriaLabelKey,
    HigSidebarSubMenuMouseOpenDelayMs,
    HigSidebarSubMenuMouseCloseDelayMs,
    HigSidebarSubMenuViewportMarginPx
} from './nv-hig-sidebar.constants';
import {
    HigAccountInfoSidebarItem,
    HigSidebarAnimationState,
    HigSidebarItem,
    HigSidebarOfflineIcon
} from './nv-hig-sidebar.type';

type SubMenuFocusTarget = 'first' | 'last' | 'next' | 'previous' | number;

interface NavItemAriaLabelCacheEntry {
    text: string;
    hasNotificationBadge: boolean;
    notificationBadgeAriaLabel?: string;
    hasPageHighlight: boolean;
    pageHighlightAriaLabel?: string;
    label: string;
}

let nextSidebarInstanceId = 0;

@Component({
    // Animation with gradient layer now is only enabled for expandable HIG Sidebar per spec
    selector: 'nv-hig-sidebar',
    templateUrl: './nv-hig-sidebar.component.html',
    styleUrls: ['./nv-hig-sidebar.component.scss'],
    animations: [
        trigger('higSidebarOpacityAnimation', [
            transition('void => *', [style({ opacity: '0' }), animate('300ms ease-in', style({ opacity: '1' }))]),
            transition('* => void', [style({ opacity: '1' }), animate('300ms ease-in', style({ opacity: '0' }))])
        ]),
        trigger('higSidebarTextAnimation', [
            transition('void => *', [
                style({ opacity: '0', transform: 'translateX(-10px)' }),
                animate('300ms ease-in', style({ opacity: '1', transform: 'translateX(0)' }))
            ]),
            transition('* => void', [
                style({ opacity: '1', transform: 'translateX(0)' }),
                animate('300ms ease-in', style({ opacity: '0', transform: 'translateX(-10px)' }))
            ])
        ])
    ],
    imports: [
        SpatialNavigationGroupDirective,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        Dir,
        CdkConnectedOverlay,
        CdkOverlayOrigin,
        EllipsifiedTextWithTooltipDirective,
        TranslateModule
    ]
})

/**
 * An adaptable sidebar can be placed on the left or right edge (RTL) and expanded or collapsed.
 * It contains functional buttons in a column, each button has customizable icon item for consumers to adjust.
 *
 * TODO: Follow the HIG spec to scope the boundary of the UI divergences.
 */
export class NvHigSidebarComponent<T> implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('higSidebarContainer') private higSidebarContainer: ElementRef<HTMLElement>;
    @ViewChild('higSidebarHighlight') private higSidebarHighlight: ElementRef<HTMLElement>;

    /** A flag to decide whether the sidebar is expandable, default is true */
    @Input() expandable = true;

    /** A flag to decide whether to display a right border around the sidebar */
    @Input() showBorder = false;

    /** A flag to decide whether to apply enlarge style on item when it gains focus */
    @Input() enlargeFocusedItem = false;

    /** A flag to decide whether to apply the gradient layer on item when it gains focus */
    @Input() gradientOnFocusedItem = false;

    /** A flag to decide whether to apply left border on selected item */
    @Input() showBorderOnSelectedItem = false;

    /** A temp flag to decide whether to hide account item when collapsed,
     * will be removed once Settings 2.0 is ready */
    @Input() hideAccountItemWhenCollapsed = false;

    /** Configuration for offline icon */
    @Input() offlineIcon: HigSidebarOfflineIcon;

    /** Custom aria-label for the gradient overlay (close menu) */
    @Input() gradientAriaLabel: string;

    /** Custom aria-label for the sidebar container (open menu) */
    @Input() sidebarAriaLabel: string;

    screenReaderEnabled: boolean;

    /** A flag to control the expand or collapse */
    @Input('expand')
    get expand(): boolean {
        return this._expand;
    }
    set expand(expand: boolean) {
        // Assignable only when expandable is true and expand value has been changed.
        if (!this.expandable || expand === this._expand) {
            return;
        }
        this._expand = expand;
        this.sidebarAnimationStateSubject.next(
            expand ? HigSidebarAnimationState.Expanding : HigSidebarAnimationState.Collapsing
        );
        this.changeDetector.markForCheck();
    }

    /** Allow consumer to decide the currentSelectedItem in sidebar */
    @Input()
    set currentSelectedItem(currentSelectedItem: HigSidebarItem<T>) {
        this._currentSelectedItem = currentSelectedItem ?? this.pageNavSidebarItems?.[0];
    }
    get currentSelectedItem(): HigSidebarItem<T> {
        return this._currentSelectedItem;
    }

    /* Distinct optional sections of items within the Sidebar,
     each section is arranged independently according to overall UI design. */
    @Input() accountInfoSidebarItem: HigAccountInfoSidebarItem<T>;
    @Input()
    set pageNavSidebarItems(items: HigSidebarItem<T>[]) {
        const nextItems = items ?? [];
        const previousOpenItem = this.subMenuOpenItem;

        if (!previousOpenItem) {
            this._pageNavSidebarItems = nextItems;
            return;
        }

        const subMenuItemElements = this.getSubMenuItemElements();
        const focusedSubMenuItemIndex = subMenuItemElements.findIndex(menuItem => menuItem === document.activeElement);
        const focusedSubMenuItemId =
            focusedSubMenuItemIndex >= 0 ? previousOpenItem.subMenuItems?.[focusedSubMenuItemIndex]?.id : undefined;

        this._pageNavSidebarItems = nextItems;
        const nextOpenItem = nextItems.find(item => item.type === previousOpenItem.type);
        if (!nextOpenItem?.subMenuItems?.length) {
            this.closeSubMenu(!!nextOpenItem && focusedSubMenuItemIndex >= 0);
            return;
        }

        this.subMenuOpenItem = nextOpenItem;
        if (focusedSubMenuItemIndex >= 0) {
            const focusedSubMenuItemNextIndex =
                focusedSubMenuItemId !== undefined
                    ? nextOpenItem.subMenuItems.findIndex(menuItem => menuItem.id === focusedSubMenuItemId)
                    : -1;
            this.focusSubMenuItem(
                null,
                focusedSubMenuItemNextIndex >= 0 ? focusedSubMenuItemNextIndex : focusedSubMenuItemIndex
            );
        }

        if (this.openSubMenuTitleKey) {
            this.openSubMenuTitleKey = nextOpenItem.text;
        }
    }
    get pageNavSidebarItems(): HigSidebarItem<T>[] {
        return this._pageNavSidebarItems;
    }
    @Input() modalNavSidebarItems: HigSidebarItem<T>[];

    /** Events that consumer can listen to and perform relevant actions */
    @Output() itemClicked = new EventEmitter<HigNavItemClick<T>>();

    /** Events emitted when a child menu row is activated */
    @Output() subMenuItemClicked = new EventEmitter<HigNavMenuItemClick<T>>();

    /** A callback function to handle offline icon click */
    @Output() offlineIconClicked = new EventEmitter<MouseEvent>();

    readonly animationStateMap = AnimationStateMap;

    HigSidebarAnimationStateType = HigSidebarAnimationState;

    get sidebarAnimationState(): HigSidebarAnimationState {
        return this.sidebarAnimationStateSubject.getValue();
    }
    private get sidebarAnimationState$(): Observable<HigSidebarAnimationState> {
        return this.sidebarAnimationStateSubject.asObservable();
    }

    get spatialNavigationEnabled(): boolean {
        return this.spatialNavigationService.enabled;
    }

    private logger: Logger;
    private destroy$ = new Subject<boolean>();
    protected isRtl: boolean;
    private prevFocusedElementBeforeNavigateIn: HTMLElement;
    private lastHighlightOffset = 0;
    private sidebarAnimationStateSubject = new BehaviorSubject<HigSidebarAnimationState>(HigSidebarAnimationState.Init);
    private screenReaderFocusClearWorkaround: boolean = false;
    private focusedItem: HTMLElement | null = null;

    private _expand = false;
    private _currentSelectedItem: HigSidebarItem<T>;
    private _pageNavSidebarItems: HigSidebarItem<T>[] = [];
    private navItemAriaLabelByItem = new WeakMap<HigSidebarItem<T>, NavItemAriaLabelCacheEntry>();

    /** Flyout submenu state: open item, trigger element, overlay positioning, and hover/focus timers. */
    subMenuOpenItem: HigSidebarItem<T> | null = null;
    openSubMenuTitleKey: string | null = null;
    subMenuPositions: ConnectedPosition[];
    readonly subMenuId = `nv-hig-sidebar-sub-menu-${nextSidebarInstanceId++}`;
    readonly subMenuPanelClass = 'hig-sidebar-sub-menu-pane';
    readonly subMenuViewportMargin = HigSidebarSubMenuViewportMarginPx;
    private openSubMenuTrigger: HTMLElement | null = null;
    private keyboardFocusedPageNavItem: HTMLElement | null = null;
    private hoveredSubMenuTrigger: HTMLElement | null = null;
    private hoveredSubMenuPanel: HTMLElement | null = null;
    private subMenuOpenTimer: number | null = null;
    private subMenuCloseTimer: number | null = null;
    private subMenuFocusTimer: number | null = null;
    private restoringSubMenuTriggerFocus = false;

    constructor(
        private builder: AnimationBuilder,
        private loggingService: LoggingService,
        private spatialNavigationService: SpatialNavigationService,
        private localeService: LocaleService,
        private ngZone: NgZone,
        private focusManager: FocusManager,
        private inputModalityService: InputModalityService,
        private changeDetector: ChangeDetectorRef,
        private accessibilityService: AccessibilityService,
        private translateService: TranslateService,
        @Optional() @Inject(NV_HIG_CONFIG) config: NvHigConfig
    ) {
        this.logger = this.loggingService.getLogger('hig/SidebarComponent');
        this.isRtl = this.localeService.isRTLLayoutEnabled;
        this.subMenuPositions = this.createSubMenuPositions();

        this.screenReaderFocusClearWorkaround = !!config?.screenReaderFocusClearWorkaround;

        this.accessibilityService
            .onScreenReaderStateChanged()
            .pipe(takeUntil(this.destroy$))
            .subscribe(isAccessibilityEnabled => {
                this.screenReaderEnabled = isAccessibilityEnabled;
            });
    }

    ngOnInit(): void {
        this.logger.info(`Init, expandable: ${this.expandable}`);
        // Ensure the class variable `isRtl` is updated whenever the locale changes during the lifecycle.
        this.localeService.localeChanged.pipe(takeUntil(this.destroy$)).subscribe(_ => {
            this.isRtl = this.localeService.isRTLLayoutEnabled;
            this.subMenuPositions = this.createSubMenuPositions(
                this.openSubMenuTrigger ? this.computeSubMenuOffsetX(this.openSubMenuTrigger) : 0
            );
            this.navItemAriaLabelByItem = new WeakMap<HigSidebarItem<T>, NavItemAriaLabelCacheEntry>();
            this.changeDetector.markForCheck();
        });
    }

    ngAfterViewInit(): void {
        if (this.spatialNavigationEnabled) {
            /**
             * Note: If need to update the binding properties with the subscriptions that are called
             * outside the Angular zone, should manually trigger the change detection to update the view in same tick.
             */
            this.ngZone.runOutsideAngular(() => {
                // Subscribe to navbeforefocus to intercept the focus before it is moved inside the sidebar
                fromEvent<NavigationEvent>(this.higSidebarContainer.nativeElement, 'navbeforefocus')
                    .pipe(
                        filter(e => e.target === this.higSidebarContainer.nativeElement),
                        takeUntil(this.destroy$)
                    )
                    .subscribe(this.onNavigateToInside.bind(this));

                fromEvent<NavigationEvent>(this.higSidebarContainer.nativeElement, 'navnotarget')
                    .pipe(
                        filter(e =>
                            e.target === this.higSidebarContainer.nativeElement
                                ? this.isRtl
                                    ? e.detail.dir === 'left'
                                    : e.detail.dir === 'right'
                                : false
                        ),
                        takeUntil(this.destroy$)
                    )
                    .subscribe(this.onNavigateToOutside.bind(this));
            });
        }
    }

    onAnimationDone(event: AnimationEvent) {
        // Update expand after collapse animation finished
        if (event.toState === 'void') {
            this.sidebarAnimationStateSubject.next(HigSidebarAnimationState.Collapsed);
            this.changeDetector.markForCheck();
        } else if (event.toState === HigSidebarAnimationState.Expanding) {
            this.sidebarAnimationStateSubject.next(HigSidebarAnimationState.Expanded);
            this.changeDetector.markForCheck();
        }
    }

    /**
     * For expandable sidebar, if a user has clicked anywhere, including on the icons, sidebar should expand.
     *
     * @param event
     */
    onSidebarClicked(event: MouseEvent): void {
        if (!this.expandable || this.expand) {
            return;
        }
        this.expand = true;
        this.moveFocusOnSelectedItem();
    }

    /**
     * for expandable sidebar, if the user clicks on the gradient layer, the sidebar should collapse.
     *
     * @param $event click event
     */
    onSidebarGradientClicked($event: MouseEvent): void {
        if (!this.expandable || !this.expand) {
            return;
        }
        this.expand = false;
        this.changeDetector.markForCheck();
    }

    onItemClicked(event: MouseEvent, itemType: T): void {
        // Consume item clicked only when expand per spec for expandable sidebar.
        if (this.expandable && !this.expand) {
            return;
        }

        // Stop dispatching the click event to the parent when the sidebar is already expanded
        // to prevent unnecessary handling in the `higSidebarContainer` click callback to cause NG0100.
        if (this.expandable && this.expand) {
            event.stopPropagation();
        }

        // When using the expandable sidebar, if click on an item that is already selected,
        // focus should be restored to the previous focus outside the sidebar and collapse sidebar.
        if (this.expandable && this.currentSelectedItem.type === itemType) {
            this.navigateOutToPrevFocused();
        }

        // on android tv with talkback enabled collapse the sidebar on item click because focus
        // is not maintained on the document when the user is navigating the ui with the accessibility focus
        if (
            this.screenReaderEnabled &&
            this.screenReaderFocusClearWorkaround &&
            this.expandable &&
            this.expand &&
            this.currentSelectedItem.type !== itemType
        ) {
            this.expand = false;
            this.changeDetector.markForCheck();
        }

        const sidebarItemClickEvent: HigNavItemClick<T> = {
            itemType,
            event
        };
        this.itemClicked.emit(sidebarItemClickEvent);
    }

    /** Closes any open submenu then delegates to the shared item-click handler. */
    onPageNavItemClick(event: MouseEvent, item: HigSidebarItem<T>): void {
        this.closeSubMenu();
        this.onItemClicked(event, item.type);
    }

    /** Reclassifies an already-focused trigger when the user switches from keyboard to pointer input. */
    onPageNavItemPointerDown(event: PointerEvent): void {
        if (event.currentTarget === this.keyboardFocusedPageNavItem) {
            this.keyboardFocusedPageNavItem = null;
        }
    }

    /** Opens a submenu when its nav item receives keyboard focus without moving focus into the panel. */
    onPageNavItemFocus(event: FocusEvent, item: HigSidebarItem<T>): void {
        const triggerElement = event.currentTarget as HTMLElement;
        this.keyboardFocusedPageNavItem =
            this.inputModalityService.lastFocusOrigin === 'keyboard' ? triggerElement : null;
        this.onItemFocus(event);
        if (this.restoringSubMenuTriggerFocus || this.keyboardFocusedPageNavItem !== triggerElement) {
            return;
        }

        if (!this.hasSubMenuItems(item)) {
            if (this.subMenuOpenItem) {
                this.closeSubMenu();
            }
            return;
        }

        this.openSubMenu(item, triggerElement);
    }

    /** Handles Escape and the outward arrow used to move focus into the submenu. */
    onPageNavItemKeydown(event: KeyboardEvent, item: HigSidebarItem<T>): void {
        if (event.key === 'Escape' && this.isSubMenuOpen(item)) {
            event.preventDefault();
            event.stopPropagation();
            this.closeSubMenu(true);
            return;
        }

        if (!this.hasSubMenuItems(item)) {
            return;
        }

        if (this.isSubMenuOpenArrowKey(event)) {
            event.preventDefault();
            event.stopPropagation();
            if (!this.isSubMenuOpen(item)) {
                this.openSubMenu(item, event.currentTarget as HTMLElement);
            }
            this.focusSubMenuItem(event.currentTarget as HTMLElement, 'first');
        }
    }

    /**
     * Starts the hover-open timer. Re-checks hover state inside the callback to avoid
     * opening the submenu if the user quickly moved the mouse through the item.
     */
    onSubMenuTriggerMouseEnter(event: MouseEvent, item: HigSidebarItem<T>): void {
        if (!this.hasSubMenuItems(item)) {
            return;
        }

        const triggerElement = event.currentTarget as HTMLElement;
        this.hoveredSubMenuTrigger = triggerElement;
        this.clearSubMenuCloseTimer();
        this.clearSubMenuOpenTimer();
        this.subMenuOpenTimer = window.setTimeout(() => {
            this.subMenuOpenTimer = null;
            const currentItem = this._pageNavSidebarItems.find(candidate => candidate.type === item.type);
            if (
                this.hoveredSubMenuTrigger === triggerElement &&
                triggerElement.isConnected &&
                currentItem?.subMenuItems?.length
            ) {
                this.openSubMenu(currentItem, triggerElement);
            }
        }, HigSidebarSubMenuMouseOpenDelayMs);
    }

    /** Cancels the open timer and schedules a close when the mouse leaves the trigger. */
    onSubMenuTriggerMouseLeave(event: MouseEvent): void {
        const triggerElement = event.currentTarget as HTMLElement;
        if (this.hoveredSubMenuTrigger !== triggerElement) {
            return;
        }

        this.hoveredSubMenuTrigger = null;
        this.clearSubMenuOpenTimer();
        this.scheduleSubMenuClose();
    }

    /** Cancels any pending close timer while the mouse is inside the submenu panel. */
    onSubMenuMouseEnter(event: MouseEvent): void {
        this.hoveredSubMenuPanel = event.currentTarget as HTMLElement;
        this.clearSubMenuCloseTimer();
    }

    /** Schedules a close when the mouse leaves the submenu panel. */
    onSubMenuMouseLeave(event: MouseEvent): void {
        const subMenuPanel = event.currentTarget as HTMLElement;
        if (this.hoveredSubMenuPanel !== subMenuPanel) {
            return;
        }

        this.hoveredSubMenuPanel = null;
        this.scheduleSubMenuClose();
    }

    /** Schedules a close unless focus is returning to the trigger or staying within the panel. */
    onSubMenuFocusOut(event: FocusEvent): void {
        const relatedTarget = event.relatedTarget as Node;
        if (this.isElementOpenSubMenuTrigger(relatedTarget) || this.isElementInOpenSubMenu(relatedTarget)) {
            return;
        }

        this.scheduleSubMenuClose();
    }

    /** Handles menu navigation keys and Escape from within the submenu. */
    onSubMenuKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.closeSubMenu(true);
            return;
        }

        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            event.stopPropagation();
            if (!this.isSubMenuOpenArrowKey(event) && this.openSubMenuTrigger?.isConnected) {
                this.focusManager.focusViaLastOrigin(this.openSubMenuTrigger);
            }
            return;
        }

        let focusTarget: SubMenuFocusTarget | null = null;
        if (event.key === 'ArrowDown') {
            focusTarget = 'next';
        } else if (event.key === 'ArrowUp') {
            focusTarget = 'previous';
        } else if (event.key === 'Home') {
            focusTarget = 'first';
        } else if (event.key === 'End') {
            focusTarget = 'last';
        }

        if (!focusTarget) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        this.focusSubMenuItem(event.target as HTMLElement, focusTarget);
    }

    /**
     * Handles Tab across trigger, panel, and outside focus, and dismisses a hover-opened submenu
     * on Escape when focus is elsewhere. The CDK overlay dispatcher receives document-level keydowns.
     */
    onSubMenuOverlayKeydown(event: KeyboardEvent): void {
        if (event.key === 'Tab') {
            this.closeSubMenu(this.isElementInOpenSubMenu(document.activeElement));
            return;
        }

        if (event.key !== 'Escape') {
            return;
        }

        if (
            this.isElementOpenSubMenuTrigger(document.activeElement) ||
            this.isElementInOpenSubMenu(document.activeElement)
        ) {
            return;
        }

        event.preventDefault();
        this.closeSubMenu();
    }

    onSubMenuItemClick(event: MouseEvent, item: HigSidebarItem<T>, subMenuItem: HigNavMenuItem): void {
        // Prevent the click from bubbling to onSidebarClicked, which would expand/collapse the sidebar.
        event.stopPropagation();
        const menuItemClick: HigNavMenuItemClick<T> = {
            event,
            itemType: item.type,
            menuItem: subMenuItem
        };

        this.closeSubMenu(event.detail === 0);
        this.subMenuItemClicked.emit(menuItemClick);
    }

    /** Reuses translations until a label-relevant item property changes. */
    getNavItemAriaLabel(item: HigSidebarItem<T>): string {
        const hasNotificationBadge = !!item.hasNotificationBadge;
        const hasPageHighlight = !!item.hasPageHighlight;
        const cachedLabel = this.navItemAriaLabelByItem.get(item);
        if (
            cachedLabel?.text === item.text &&
            cachedLabel.hasNotificationBadge === hasNotificationBadge &&
            cachedLabel.notificationBadgeAriaLabel === item.notificationBadgeAriaLabel &&
            cachedLabel.hasPageHighlight === hasPageHighlight &&
            cachedLabel.pageHighlightAriaLabel === item.pageHighlightAriaLabel
        ) {
            return cachedLabel.label;
        }

        const translatedItem = this.translateService.instant(item.text);
        let labelKey: string | null = null;
        if (hasPageHighlight) {
            labelKey = item.pageHighlightAriaLabel || HigSidebarItemWithPageHighlightAriaLabelKey;
        } else if (hasNotificationBadge) {
            labelKey = item.notificationBadgeAriaLabel || HigSidebarItemWithNotificationAriaLabelKey;
        }
        const label = labelKey ? this.translateService.instant(labelKey, { item: translatedItem }) : translatedItem;
        this.navItemAriaLabelByItem.set(item, {
            text: item.text,
            hasNotificationBadge,
            notificationBadgeAriaLabel: item.notificationBadgeAriaLabel,
            hasPageHighlight,
            pageHighlightAriaLabel: item.pageHighlightAriaLabel,
            label
        });
        return label;
    }

    trackByNavItemType(_index: number, item: HigSidebarItem<T>): T {
        return item.type;
    }

    /** Returns true when the nav item has at least one submenu entry. */
    hasSubMenuItems(item: HigSidebarItem<T>): boolean {
        return !!item.subMenuItems?.length;
    }

    /** Returns true when the submenu for this specific item is currently open. */
    isSubMenuOpen(item: HigSidebarItem<T>): boolean {
        return this.hasSubMenuItems(item) && this.subMenuOpenItem?.type === item.type;
    }

    /** Resets submenu state and optionally restores focus to its trigger. */
    closeSubMenu(restoreTriggerFocus = false): void {
        const triggerElement = restoreTriggerFocus ? this.openSubMenuTrigger : null;
        this.clearSubMenuOpenTimer();
        this.clearSubMenuCloseTimer();
        this.clearSubMenuFocusTimer();
        this.subMenuOpenItem = null;
        this.openSubMenuTitleKey = null;
        this.openSubMenuTrigger = null;
        this.subMenuPositions = this.createSubMenuPositions();
        this.hoveredSubMenuPanel = null;
        this.hoveredSubMenuTrigger = null;
        this.changeDetector.markForCheck();

        if (triggerElement?.isConnected && document.activeElement !== triggerElement) {
            this.restoringSubMenuTriggerFocus = true;
            try {
                this.focusManager.focusViaLastOrigin(triggerElement);
            } finally {
                this.restoringSubMenuTriggerFocus = false;
            }
        }
    }

    onItemFocus(event: FocusEvent) {
        if (this.higSidebarHighlight) {
            this.higSidebarHighlight.nativeElement.style.height = `${
                (event.target as HTMLButtonElement).getBoundingClientRect().height
            }px`;

            const animation = animate(
                this.expandable && //The focus animation is only effective for the expandable sideba
                    this.sidebarAnimationState === HigSidebarAnimationState.Expanded && // Skip animations on expending
                    this.inputModalityService.lastFocusOrigin === 'keyboard'
                    ? '200ms ease-in'
                    : 0,
                keyframes([
                    style({ transform: `translateY(${this.lastHighlightOffset}px)` }),
                    style({
                        transform: `translateY(${(event.target as HTMLButtonElement).getBoundingClientRect().top}px)`
                    })
                ])
            );

            this.lastHighlightOffset = (event.target as HTMLButtonElement).getBoundingClientRect().top;
            const player = this.builder.build(animation).create(this.higSidebarHighlight.nativeElement);
            player.play();
        }
    }

    /**
     * Collapse the sidebar when focus is out of the sidebar
     */
    onFocusOut(event: FocusEvent): void {
        // Ensure that moving focus around the buttons in the sidebar doesn't trigger
        // the collapse action by checking whether the relatedTarget is in the sidebar.
        if (
            !this.isFocusedElementInSidebar(event.relatedTarget as Node) &&
            !this.isElementInOpenSubMenu(event.relatedTarget as Node)
        ) {
            // Check if focus is moving to a dialog/modal container to prevent sidebar collapse
            // This fixes the issue where TV help button opens dialog and sidebar collapses unexpectedly
            if (this.isFocusMovingToDialog(event.relatedTarget as Node)) {
                return;
            }

            // for android tv with talkback enabled the focus will be removed from the document when
            // navigating to the next accessibility focusable element
            // if accessibility is enabled and the focusOut event is removing the focus from the
            // document (relatedTarget === null) then ignore the event.
            if (
                this.screenReaderEnabled &&
                this.screenReaderFocusClearWorkaround &&
                this.expandable &&
                event.relatedTarget === null
            ) {
                return;
            }

            if (this.expandable && this.expand) {
                this.expand = false;
                // handle cases we open a new dialog and transit focus in the dialog's after view init hook
                this.changeDetector.detectChanges();
            }
        }
    }

    /**
     * Expand the sidebar when focus is in the sidebar
     */
    onFocusIn(event: FocusEvent): void {
        const isKeyboardFocus = this.inputModalityService.lastFocusOrigin === 'keyboard';
        const hasScreenReaderWorkaround = this.screenReaderEnabled && this.screenReaderFocusClearWorkaround;

        // Only keyboard focus should trigger expansion when screen reader workaround is not active
        // This prevents a ping-pong effect between focus-based expansion and screen reader collapse
        const shouldTriggerExpansion = this.expandable && !this.expand && isKeyboardFocus && !hasScreenReaderWorkaround;

        if (shouldTriggerExpansion) {
            this.expand = true;
            this.changeDetector.markForCheck();
        }
    }

    /**
     * Track focused item for aria-hidden calculations
     */
    onItemFocusIn(event: FocusEvent): void {
        this.focusedItem = event.target as HTMLElement;
    }

    /** Handles submenu focus departure before clearing item focus tracking. */
    onItemFocusOut(event: FocusEvent, item?: HigSidebarItem<T>): void {
        if (event.currentTarget === this.keyboardFocusedPageNavItem) {
            this.keyboardFocusedPageNavItem = null;
        }
        if (item && this.hasSubMenuItems(item)) {
            this.onSubMenuFocusOut(event);
        }
        this.focusedItem = null;
    }

    /**
     * Handle the offline icon click
     */
    onOfflineIconClick(event: MouseEvent): void {
        this.offlineIconClicked.emit(event);
    }

    /**
     * Programmatically move focus to current selected item in expandable sidebar
     *
     * @param dir the direction of focus movement to call `navigateTo`,
     * if not provided, will call `focusViaLastOrigin` on current selected item directly.
     */
    moveFocusOnSelectedItem(dir?: SpatialNavigationDirection): void {
        setTimeout(() => {
            const currentSelectedButton = this.higSidebarContainer?.nativeElement.querySelector(
                '.selected'
            ) as HTMLElement;
            const currentFocusedElement = this.focusManager.currentFocusedElement;

            if (!currentSelectedButton || currentSelectedButton === currentFocusedElement) {
                return;
            }

            if (this.getSiblingElement()?.contains(currentFocusedElement)) {
                this.prevFocusedElementBeforeNavigateIn = currentFocusedElement;
            }

            if (this.expandable && !this.expand) {
                this.expand = true;
                // run change detection because focus is about to move
                this.changeDetector.detectChanges();
            }

            if (dir) {
                this.focusManager.navigateTo(currentSelectedButton, dir);
            } else {
                this.focusManager.focusViaLastOrigin(currentSelectedButton);
            }
        });
    }

    /**
     * Handle the focus movement inside the sidebar by navigation.
     * - set the previous focused element before navigate in only when the
     *   current focused element is in the sibling of the parent that contains the sidebar.
     */
    private onNavigateToInside(event: NavigationEvent): void {
        event.preventDefault();
        this.onExpandSidebarByNavigationIfNeeded();
        this.moveFocusOnSelectedItem(event.detail.dir);
    }

    private onExpandSidebarByNavigationIfNeeded(): void {
        const invalidSidebarAnimationState =
            this.sidebarAnimationState !== HigSidebarAnimationState.Collapsed &&
            this.sidebarAnimationState !== HigSidebarAnimationState.Init;
        if (!this.expandable || this.expand || invalidSidebarAnimationState) {
            return;
        }
        this.expand = true;
        this.changeDetector.markForCheck();
    }
    /**
     * Handle the focus movement out from sidebar when no target in right / left(RTL) by navigation
     */
    private onNavigateToOutside(event: NavigationEvent): void {
        event.preventDefault();
        // no op if the sidebar is still expanding to prevent NG0100 exception
        if (this.expandable && this.sidebarAnimationState !== HigSidebarAnimationState.Expanded) {
            return;
        }
        this.navigateOutToPrevFocused();
    }

    /**
     * Move focus to the previous focused element in current content
     * - if is not connected to DOM, fallback to find nearest focusable element in current content.
     */
    private navigateOutToPrevFocused() {
        const navigateOutDir = this.isRtl ? 'left' : 'right';
        if (this.prevFocusedElementBeforeNavigateIn && this.prevFocusedElementBeforeNavigateIn.isConnected) {
            this.focusManager.navigateTo(this.prevFocusedElementBeforeNavigateIn, navigateOutDir);
        } else {
            this.handleNavigateToNearBy(navigateOutDir);
        }
        this.prevFocusedElementBeforeNavigateIn = null;
    }

    /**
     * Handle the navigation to nearby element when the previous focused element is not connected to DOM.
     *
     * For expandable sidebar
     * - Close the sidebar and navigate to nearby element when sidebar animation is completed.
     *
     * For non-expandable sidebar
     * - Navigate to nearby element.
     */
    private handleNavigateToNearBy(dir: SpatialNavigationDirection): void {
        if (!this.expandable || !this.expand) {
            this.navigateToNearby(dir);
            return;
        }
        // Subscribe the animation state, navigate to nearby only when sidebar has been collapsed,
        // unsubscribe after received the first collapsed emission.
        this.sidebarAnimationState$
            .pipe(
                filter(state => state === HigSidebarAnimationState.Collapsed),
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe(_ => {
                this.navigateToNearby(this.isRtl ? 'left' : 'right');
            });
        this.expand = false;
        this.changeDetector.markForCheck();
    }

    /**
     * Move focus to the nearest focusable element in current sibling.
     *
     * @param dir the direction of focus movement to call `navigateTo`
     */
    private navigateToNearby(dir: SpatialNavigationDirection): void {
        const currentFocusElement = document.activeElement as HTMLElement;
        const focusRectBeforeNavigate = currentFocusElement.getBoundingClientRect();

        const collapsedSidebarRect = this.higSidebarContainer.nativeElement.getBoundingClientRect();
        const collapsedSidebarEdge = this.isRtl ? collapsedSidebarRect.left : collapsedSidebarRect.right;
        this.spatialNavigationService.setStartingPointAt(
            collapsedSidebarEdge,
            (focusRectBeforeNavigate.top + focusRectBeforeNavigate.bottom) / 2
        );
        // The start point has been set, getClosestChild will locate element from target element based on the coordinates
        // regardless of the source element, feeding document.body is sufficient for this case.
        const closestElement = this.spatialNavigationService.getClosestChild(
            document.body,
            this.getSiblingElement(),
            dir
        );
        this.focusManager.navigateTo(closestElement, dir);
    }

    /** Opens the submenu and computes position-specific offsets from the current trigger geometry. */
    private openSubMenu(item: HigSidebarItem<T>, triggerElement: HTMLElement): void {
        this.clearSubMenuOpenTimer();
        this.clearSubMenuCloseTimer();
        this.clearSubMenuFocusTimer();
        this.subMenuOpenItem = item;
        this.openSubMenuTrigger = triggerElement;
        this.openSubMenuTitleKey = this.isRailLabelTruncated(triggerElement) ? item.text : null;
        this.subMenuPositions = this.createSubMenuPositions(this.computeSubMenuOffsetX(triggerElement));
        this.changeDetector.markForCheck();
    }

    /**
     * Schedules a close after a short delay. Re-checks hover and focus state inside
     * the callback because the timer fires asynchronously and state may have changed by then.
     */
    private scheduleSubMenuClose(): void {
        if (!this.subMenuOpenItem) return;
        this.clearSubMenuCloseTimer();
        this.subMenuCloseTimer = window.setTimeout(() => {
            this.subMenuCloseTimer = null;
            // Trigger focus holds the menu open only for keyboard modality; a mouse-focused
            // trigger (still focused after a click) must not pin a hover-opened menu
            // after the pointer leaves. Focus inside the panel always holds it open.
            const hasKeyboardTriggerFocus =
                this.keyboardFocusedPageNavItem === this.openSubMenuTrigger &&
                this.isElementOpenSubMenuTrigger(document.activeElement);
            const hasSubMenuFocus = hasKeyboardTriggerFocus || this.isElementInOpenSubMenu(document.activeElement);
            const hasOpenTriggerHover = this.isElementOpenSubMenuTrigger(this.hoveredSubMenuTrigger);
            const hasOpenSubMenuHover = this.isElementInOpenSubMenu(this.hoveredSubMenuPanel);
            if (hasOpenTriggerHover || hasOpenSubMenuHover || hasSubMenuFocus) {
                return;
            }

            this.closeSubMenu();
        }, HigSidebarSubMenuMouseCloseDelayMs);
    }

    private clearSubMenuCloseTimer(): void {
        if (this.subMenuCloseTimer !== null) {
            clearTimeout(this.subMenuCloseTimer);
            this.subMenuCloseTimer = null;
        }
    }

    private clearSubMenuOpenTimer(): void {
        if (this.subMenuOpenTimer !== null) {
            clearTimeout(this.subMenuOpenTimer);
            this.subMenuOpenTimer = null;
        }
    }

    private clearSubMenuFocusTimer(): void {
        if (this.subMenuFocusTimer !== null) {
            clearTimeout(this.subMenuFocusTimer);
            this.subMenuFocusTimer = null;
        }
    }

    /** Moves focus within the submenu, deferring only when the overlay or refreshed rows must render first. */
    private focusSubMenuItem(currentElement: HTMLElement | null, target: SubMenuFocusTarget): void {
        this.clearSubMenuFocusTimer();

        const moveFocus = (): void => {
            if (
                currentElement &&
                document.activeElement !== currentElement &&
                document.activeElement !== document.body
            ) {
                return;
            }

            const menuItems = this.getSubMenuItemElements();
            if (!menuItems.length) {
                return;
            }

            const currentIndex = menuItems.findIndex(
                menuItem => menuItem === (currentElement ?? document.activeElement)
            );
            let nextIndex: number;
            if (typeof target === 'number') {
                if (document.activeElement !== document.body) {
                    return;
                }
                nextIndex = Math.min(Math.max(target, 0), menuItems.length - 1);
            } else if (target === 'first') {
                nextIndex = 0;
            } else if (target === 'last') {
                nextIndex = menuItems.length - 1;
            } else if (currentIndex === -1) {
                nextIndex = target === 'previous' ? menuItems.length - 1 : 0;
            } else {
                const direction = target === 'previous' ? -1 : 1;
                nextIndex = (currentIndex + direction + menuItems.length) % menuItems.length;
            }
            this.focusManager.focusViaLastOrigin(menuItems[nextIndex]);
        };

        if (typeof target !== 'number' && this.isElementInOpenSubMenu(currentElement)) {
            moveFocus();
            return;
        }

        this.subMenuFocusTimer = window.setTimeout(() => {
            this.subMenuFocusTimer = null;
            moveFocus();
        });
    }

    private getSubMenuItemElements(): HTMLButtonElement[] {
        const subMenuElement = document.getElementById(this.subMenuId);
        return Array.from(subMenuElement?.querySelectorAll<HTMLButtonElement>('.hig-sidebar-sub-menu-item') ?? []);
    }

    /** Returns true for the arrow key that opens the submenu outward (right in LTR, left in RTL). */
    private isSubMenuOpenArrowKey(event: KeyboardEvent): boolean {
        const outwardArrowKey = this.isRtl ? 'ArrowLeft' : 'ArrowRight';
        return event.key === outwardArrowKey;
    }

    /** Returns true when the nav item's text label is clipped in collapsed rail mode. */
    private isRailLabelTruncated(triggerElement: HTMLElement): boolean {
        const textElement = triggerElement.querySelector('.item-text') as HTMLElement;
        return textElement ? this.isElementOverflowing(textElement) : false;
    }

    private isElementOverflowing(element: HTMLElement): boolean {
        return (
            Math.ceil(element.scrollWidth) > Math.ceil(element.clientWidth) ||
            Math.ceil(element.scrollHeight) > Math.ceil(element.clientHeight)
        );
    }

    /** Returns true when the given node is within the button that triggered the open submenu. */
    private isElementOpenSubMenuTrigger(currentFocusElement: Node | null): boolean {
        return !!currentFocusElement && !!this.openSubMenuTrigger?.contains(currentFocusElement);
    }

    /** Returns true when the given node is inside the open submenu panel. */
    private isElementInOpenSubMenu(currentFocusElement: Node | null): boolean {
        if (!currentFocusElement) {
            return false;
        }

        return document.getElementById(this.subMenuId)?.contains(currentFocusElement) ?? false;
    }

    /**
     * Computes the horizontal offset needed to align the submenu panel's near edge
     * with the sidebar container's edge, accounting for RTL layout.
     * Called once when the submenu opens; the result is stored on its position entries.
     */
    private computeSubMenuOffsetX(triggerElement: HTMLElement): number {
        const sidebar = this.higSidebarContainer?.nativeElement;
        if (!sidebar) {
            return 0;
        }

        const triggerRect = triggerElement.getBoundingClientRect();
        const sidebarRect = sidebar.getBoundingClientRect();

        return this.isRtl ? sidebarRect.left - triggerRect.left : sidebarRect.right - triggerRect.right;
    }

    /**
     * Returns direction-aware CDK positions. The first two open outward from the rail;
     * the final two let CDK flip the panel when the viewport has insufficient space.
     */
    private createSubMenuPositions(primaryOffsetX = 0): ConnectedPosition[] {
        return [
            {
                originX: 'end',
                originY: 'top',
                overlayX: 'start',
                overlayY: 'top',
                offsetX: primaryOffsetX
            },
            {
                originX: 'end',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'bottom',
                offsetX: primaryOffsetX
            },
            {
                originX: 'start',
                originY: 'top',
                overlayX: 'end',
                overlayY: 'top',
                offsetX: -primaryOffsetX
            },
            {
                originX: 'start',
                originY: 'bottom',
                overlayX: 'end',
                overlayY: 'bottom',
                offsetX: -primaryOffsetX
            }
        ];
    }

    private isFocusedElementInSidebar(currentFocusElement: Node | null): boolean {
        return !!currentFocusElement && this.higSidebarContainer.nativeElement.contains(currentFocusElement);
    }

    /**
     * Check if focus is moving to a dialog or modal container
     * This prevents sidebar collapse when dialogs are opened from sidebar buttons
     */
    private isFocusMovingToDialog(relatedTarget: Node | null): boolean {
        if (!relatedTarget) {
            return false;
        }

        const element = relatedTarget as Element;
        if (!element.closest) {
            return false;
        }

        // Check if the related target is within the MDC dialog container
        return element.closest('.mat-mdc-dialog-container') !== null;
    }

    /**
     * Get the sibling element of the parent that contains the sidebar
     */
    private getSiblingElement(): HTMLElement {
        const parent = this.higSidebarContainer.nativeElement.parentElement;
        return parent.nextElementSibling as HTMLElement;
    }

    /**
     * Calculate the aria-hidden value for sidebar items based on expandable state and animation
     * @param buttonOrigin - The sidebar item button origin to check focus for
     * @param hideWhenCollapsed - Whether to hide the item when sidebar is collapsed
     * @returns The aria-hidden value or null
     */
    getAriaHiddenForItem(buttonOrigin: CdkOverlayOrigin, hideWhenCollapsed: boolean): boolean | null {
        let result: boolean | null = null;

        if (this.expandable) {
            const isExpanded = this.animationStateMap[this.sidebarAnimationState].expand;

            // Hide button when sidebar is collapsed and hideWhenCollapsed is true
            result = !isExpanded && hideWhenCollapsed;

            // WORKAROUND: Prevent aria-hidden="true" on focused elements
            // Chromium ignores the aria-hidden attribute when set on an element that already
            // has focus. This causes the element to remain focusable despite being marked as
            // hidden, breaking the intended accessibility behavior.
            if (result && this.focusedItem) {
                if (buttonOrigin.elementRef.nativeElement === this.focusedItem) {
                    result = false;
                }
            }
        }

        return result;
    }
    ngOnDestroy(): void {
        this.clearSubMenuOpenTimer();
        this.clearSubMenuCloseTimer();
        this.clearSubMenuFocusTimer();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
